import React, {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from "react";
import axios from "axios";

/*
 * =========================================================
 * APIs
 * =========================================================
 */

const SHOP_API =
    "https://oppiesfunc.azurewebsites.net/api/getShopByEmail?code=7KWINbztw9fLYDfl2dC2hQ-oOEljDDO170DmZm61jttOAzFuZHRDLw==";

const UPDATE_HOURS_API =
    "https://oppiesfunc.azurewebsites.net/api/updateShopHours?code=g4cBDc318ERtXKF4UCWMp9YrwCt7HJuauKL0M8DOqYMoAzFuyoz4BA==";

const GET_MANAGED_SHOPS_API =
    "https://oppiesfunc.azurewebsites.net/api/getManagedShops?code=huECMxImipiK72eFqefelG3Vcf77HzRyN_x22uPFI5UHAzFuRPLsYw==";


/*
 * =========================================================
 * DAY CONFIGURATION
 * =========================================================
 *
 * UI uses friendly full day names.
 *
 * Cosmos uses the existing OPPIES schema:
 *
 * Mon
 * Tues
 * Wed
 * Thur
 * Fri
 * Sat
 * Sun
 *
 * IMPORTANT:
 * We preserve that Cosmos schema.
 */

const DAYS = [
    {
        label: "Monday",
        cosmosKey: "Mon",
    },
    {
        label: "Tuesday",
        cosmosKey: "Tues",
    },
    {
        label: "Wednesday",
        cosmosKey: "Wed",
    },
    {
        label: "Thursday",
        cosmosKey: "Thur",
    },
    {
        label: "Friday",
        cosmosKey: "Fri",
    },
    {
        label: "Saturday",
        cosmosKey: "Sat",
    },
    {
        label: "Sunday",
        cosmosKey: "Sun",
    },
];

const DAYS_OF_WEEK =
    DAYS.map(
        (day) =>
            day.label
    );


const createEmptyHours = () =>
    DAYS_OF_WEEK.reduce(
        (result, day) => {

            result[day] = "";

            return result;
        },
        {}
    );


const ShopHoursTab = ({
    shopId,
    userEmail,
}) => {

    /*
     * =========================================================
     * STATE
     * =========================================================
     */

    const [hours, setHours] =
        useState(
            createEmptyHours
        );

    const [savedHours, setSavedHours] =
        useState(
            createEmptyHours
        );

    const [status, setStatus] =
        useState({
            type: "",
            message: "",
        });

    const [loading, setLoading] =
        useState(true);

    const [saving, setSaving] =
        useState(false);

    const [
        activeShopEmail,
        setActiveShopEmail,
    ] = useState("");

    const [
        loadedShopName,
        setLoadedShopName,
    ] = useState("");


    /*
     * =========================================================
     * LOGGED-IN ACCOUNT
     * =========================================================
     */

    const authenticatedEmail =
        (
            userEmail ||
            localStorage.getItem(
                "shopEmail"
            ) ||
            ""
        )
            .trim()
            .toLowerCase();


    const accountShopId =
        localStorage.getItem(
            "shopId"
        ) || "";


    /*
     * =========================================================
     * STATUS
     * =========================================================
     */

    const showStatus = (
        type,
        message
    ) => {

        setStatus({
            type,
            message,
        });
    };


    const clearStatus = () => {

        setStatus({
            type: "",
            message: "",
        });
    };


    /*
     * =========================================================
     * COSMOS -> UI HOURS
     * =========================================================
     *
     * Cosmos:
     *
     * {
     *   Mon: "Closed",
     *   Tues: "9:00am - 5:00pm",
     *   Wed: "11:00am - 3:00pm",
     *   Thur: "Closed",
     *   Fri: "Closed",
     *   Sat: "Closed",
     *   Sun: "Closed"
     * }
     *
     * UI:
     *
     * {
     *   Monday: "Closed",
     *   Tuesday: "9:00am - 5:00pm",
     *   ...
     * }
     */

    const normaliseHours = (
        source = {}
    ) => {

        return DAYS.reduce(
            (
                result,
                day
            ) => {

                /*
                 * Primary value:
                 * existing Cosmos short key.
                 *
                 * Fallback:
                 * full day name.
                 *
                 * This gives us backwards compatibility if
                 * any newer document already contains full
                 * day names.
                 */

                const value =
                    source?.[
                    day.cosmosKey
                    ] ??
                    source?.[
                    day.label
                    ] ??
                    "";


                result[
                    day.label
                ] =
                    typeof value ===
                        "string"
                        ? value
                        : "";


                return result;
            },
            {}
        );
    };


    /*
     * =========================================================
     * UI -> COSMOS HOURS
     * =========================================================
     *
     * Converts full display names back to the existing OPPIES
     * short-key Cosmos schema when saving.
     */

    const buildCosmosHours = (
        uiHours = {}
    ) => {

        return DAYS.reduce(
            (
                result,
                day
            ) => {

                result[
                    day.cosmosKey
                ] =
                    String(
                        uiHours?.[
                        day.label
                        ] ||
                        ""
                    ).trim();


                return result;
            },
            {}
        );
    };


    /*
     * =========================================================
     * RESOLVE ACTIVE SHOP
     * =========================================================
     */

    const resolveActiveShopEmail =
        useCallback(
            async () => {

                if (
                    !authenticatedEmail
                ) {

                    throw new Error(
                        "Your account email is unavailable. Please sign in again."
                    );
                }


                if (!shopId) {

                    throw new Error(
                        "Your shop ID is unavailable. Please sign in again."
                    );
                }


                /*
                 * Normal account / manager's own original
                 * shop.
                 */

                if (
                    accountShopId &&
                    shopId ===
                    accountShopId
                ) {

                    return {
                        email:
                            authenticatedEmail,

                        name:
                            localStorage.getItem(
                                "shopName"
                            ) ||
                            "",
                    };
                }


                /*
                 * Manager selected branch.
                 */

                try {

                    const separator =
                        GET_MANAGED_SHOPS_API.includes(
                            "?"
                        )
                            ? "&"
                            : "?";


                    const url =
                        `${GET_MANAGED_SHOPS_API}` +
                        `${separator}email=${encodeURIComponent(
                            authenticatedEmail
                        )}`;


                    const response =
                        await axios.get(
                            url,
                            {
                                params: {
                                    _t:
                                        Date.now(),
                                },

                                headers: {
                                    "Cache-Control":
                                        "no-cache",
                                },
                            }
                        );


                    if (
                        response.data
                            ?.manager !==
                        true
                    ) {

                        return {
                            email:
                                authenticatedEmail,

                            name:
                                localStorage.getItem(
                                    "shopName"
                                ) ||
                                "",
                        };
                    }


                    const managedShops =
                        Array.isArray(
                            response.data
                                ?.shops
                        )
                            ? response
                                .data
                                .shops
                            : [];


                    const selectedShop =
                        managedShops.find(
                            (shop) =>
                                shop
                                    ?.shopId ===
                                shopId
                        );


                    if (
                        !selectedShop
                    ) {

                        throw new Error(
                            "The selected shop is not available to this manager account."
                        );
                    }


                    if (
                        !selectedShop.email
                    ) {

                        throw new Error(
                            "The selected shop does not have an email address available."
                        );
                    }


                    return {
                        email:
                            String(
                                selectedShop.email
                            )
                                .trim()
                                .toLowerCase(),

                        name:
                            selectedShop.name ||
                            selectedShop.shopId ||
                            "",
                    };


                } catch (err) {

                    /*
                     * Normal accounts receive 403 from
                     * getManagedShops.
                     */

                    if (
                        err?.response
                            ?.status ===
                        403
                    ) {

                        return {
                            email:
                                authenticatedEmail,

                            name:
                                localStorage.getItem(
                                    "shopName"
                                ) ||
                                "",
                        };
                    }


                    throw err;
                }

            },
            [
                authenticatedEmail,
                accountShopId,
                shopId,
            ]
        );


    /*
     * =========================================================
     * LOAD CURRENT COSMOS HOURS
     * =========================================================
     */

    const fetchExistingHours =
        useCallback(
            async () => {

                if (!shopId) {

                    setLoading(
                        false
                    );

                    showStatus(
                        "error",
                        "Your shop ID is unavailable. Please sign in again."
                    );

                    return;
                }


                if (
                    !authenticatedEmail
                ) {

                    setLoading(
                        false
                    );

                    showStatus(
                        "error",
                        "Your account email is unavailable. Please sign in again."
                    );

                    return;
                }


                try {

                    setLoading(
                        true
                    );

                    clearStatus();


                    /*
                     * Prevent old branch hours remaining visible
                     * while a manager switches branch.
                     */

                    const blank =
                        createEmptyHours();


                    setHours(
                        blank
                    );

                    setSavedHours(
                        blank
                    );


                    /*
                     * Resolve selected branch email.
                     */

                    const selectedShop =
                        await resolveActiveShopEmail();


                    const selectedEmail =
                        selectedShop
                            ?.email
                            ?.trim()
                            .toLowerCase();


                    if (
                        !selectedEmail
                    ) {

                        throw new Error(
                            "Unable to determine the selected shop email."
                        );
                    }


                    setActiveShopEmail(
                        selectedEmail
                    );

                    setLoadedShopName(
                        selectedShop
                            ?.name ||
                        ""
                    );


                    /*
                     * Existing OPPIES shop lookup.
                     */

                    const res =
                        await axios.post(
                            SHOP_API,
                            {
                                email:
                                    selectedEmail,
                            },
                            {
                                params: {
                                    _t:
                                        Date.now(),
                                },

                                headers: {
                                    "Cache-Control":
                                        "no-cache",

                                    Pragma:
                                        "no-cache",
                                },
                            }
                        );


                    /*
                     * Existing Cosmos hours:
                     *
                     * {
                     *   Mon,
                     *   Tues,
                     *   Wed,
                     *   Thur,
                     *   Fri,
                     *   Sat,
                     *   Sun
                     * }
                     */

                    const cosmosHours =
                        res.data
                            ?.hours ||
                        {};


                    console.log(
                        "📅 Raw Cosmos hours:",
                        cosmosHours
                    );


                    /*
                     * Translate Cosmos keys into full UI
                     * day names.
                     */

                    const currentHours =
                        normaliseHours(
                            cosmosHours
                        );


                    console.log(
                        "📅 Normalised UI hours:",
                        currentHours
                    );


                    setHours(
                        currentHours
                    );

                    setSavedHours(
                        currentHours
                    );


                    if (
                        res.data
                            ?.name
                    ) {

                        setLoadedShopName(
                            res.data.name
                        );
                    }


                    console.log(
                        "✅ Shop Hours loaded from Cosmos:",
                        {
                            shopId,

                            email:
                                selectedEmail,

                            name:
                                res.data
                                    ?.name ||
                                selectedShop
                                    ?.name ||
                                "",

                            rawHours:
                                cosmosHours,

                            displayHours:
                                currentHours,
                        }
                    );


                } catch (err) {

                    console.error(
                        "❌ Error fetching current hours:",
                        err?.response
                            ?.data ||
                        err?.message ||
                        err
                    );


                    showStatus(
                        "error",
                        err?.response
                            ?.data
                            ?.error ||
                        err?.message ||
                        "We couldn't load this shop's current opening hours."
                    );


                } finally {

                    setLoading(
                        false
                    );
                }

            },
            [
                shopId,
                authenticatedEmail,
                resolveActiveShopEmail,
            ]
        );


    /*
     * =========================================================
     * RELOAD WHEN SHOP CHANGES
     * =========================================================
     */

    useEffect(
        () => {

            fetchExistingHours();

        },
        [
            fetchExistingHours,
        ]
    );


    /*
     * =========================================================
     * DIRTY STATE
     * =========================================================
     */

    const hasChanges =
        useMemo(
            () =>
                DAYS_OF_WEEK.some(
                    (day) =>
                        (
                            hours[
                            day
                            ] ||
                            ""
                        ) !==
                        (
                            savedHours[
                            day
                            ] ||
                            ""
                        )
                ),
            [
                hours,
                savedHours,
            ]
        );


    /*
     * =========================================================
     * UPDATE DAY
     * =========================================================
     */

    const updateDay = (
        day,
        value
    ) => {

        setHours(
            (
                current
            ) => ({
                ...current,

                [day]:
                    value,
            })
        );


        if (
            status.message
        ) {

            clearStatus();
        }
    };


    /*
     * =========================================================
     * CLOSED
     * =========================================================
     */

    const toggleClosed = (
        day
    ) => {

        setHours(
            (
                current
            ) => {

                const currentlyClosed =
                    current[
                        day
                    ]
                        ?.trim()
                        .toLowerCase() ===
                    "closed";


                return {
                    ...current,

                    [day]:
                        currentlyClosed
                            ? ""
                            : "Closed",
                };
            }
        );


        if (
            status.message
        ) {

            clearStatus();
        }
    };


    /*
     * =========================================================
     * COPY MONDAY
     * =========================================================
     */

    const copyMondayToWeekdays =
        () => {

            const monday =
                hours.Monday ||
                "";


            if (
                !monday.trim()
            ) {

                showStatus(
                    "warning",
                    "Enter Monday's hours first, then you can copy them to the other weekdays."
                );

                return;
            }


            setHours(
                (
                    current
                ) => ({
                    ...current,

                    Tuesday:
                        monday,

                    Wednesday:
                        monday,

                    Thursday:
                        monday,

                    Friday:
                        monday,
                })
            );


            showStatus(
                "info",
                "Monday's hours copied to Tuesday–Friday. Save your changes when ready."
            );
        };


    /*
     * =========================================================
     * RESET
     * =========================================================
     */

    const resetChanges =
        () => {

            setHours({
                ...savedHours,
            });


            showStatus(
                "info",
                "Unsaved changes have been reset."
            );
        };


    /*
     * =========================================================
     * REFRESH
     * =========================================================
     */

    const refreshHours =
        async () => {

            if (
                loading ||
                saving
            ) {

                return;
            }


            await fetchExistingHours();
        };


    /*
     * =========================================================
     * SAVE
     * =========================================================
     */

    const handleUpdate =
        async () => {

            if (saving) {
                return;
            }


            if (!shopId) {

                showStatus(
                    "error",
                    "Your shop ID is unavailable. Please sign in again."
                );

                return;
            }


            if (
                !authenticatedEmail
            ) {

                showStatus(
                    "error",
                    "Your account email is unavailable. Please sign in again."
                );

                return;
            }


            /*
             * Convert UI values back into the existing Cosmos
             * structure.
             */

            const cosmosHours =
                buildCosmosHours(
                    hours
                );


            console.log(
                "📤 Hours being saved to Cosmos:",
                cosmosHours
            );


            try {

                setSaving(
                    true
                );


                showStatus(
                    "info",
                    "Saving your opening hours..."
                );


                /*
                 * Existing Cosmos shape is preserved:
                 *
                 * {
                 *   Mon,
                 *   Tues,
                 *   Wed,
                 *   Thur,
                 *   Fri,
                 *   Sat,
                 *   Sun
                 * }
                 */

                await axios.post(
                    UPDATE_HOURS_API,
                    {
                        shopId,

                        hours:
                            cosmosHours,

                        /*
                         * Logged-in account identity.
                         *
                         * Normal shop:
                         *   their own email
                         *
                         * Manager:
                         *   Head Office login email
                         */

                        userEmail:
                            authenticatedEmail,
                    }
                );


                /*
                 * Keep UI state in friendly full-day format.
                 */

                const cleanedUiHours =
                    normaliseHours(
                        cosmosHours
                    );


                setHours(
                    cleanedUiHours
                );


                setSavedHours(
                    cleanedUiHours
                );


                showStatus(
                    "success",
                    "Opening hours updated successfully."
                );


                console.log(
                    "✅ Shop Hours saved:",
                    {
                        shopId,

                        shopEmail:
                            activeShopEmail,

                        userEmail:
                            authenticatedEmail,

                        hours:
                            cosmosHours,
                    }
                );


            } catch (err) {

                console.error(
                    "❌ Failed to update hours:",
                    err?.response
                        ?.data ||
                    err?.message ||
                    err
                );


                if (
                    err?.response
                        ?.status ===
                    403
                ) {

                    showStatus(
                        "error",
                        err?.response
                            ?.data
                            ?.error ||
                        "You are not authorised to manage this shop."
                    );

                    return;
                }


                showStatus(
                    "error",
                    err?.response
                        ?.data
                        ?.error ||
                    "We couldn't update your opening hours. Please try again."
                );


            } finally {

                setSaving(
                    false
                );
            }
        };


    /*
     * =========================================================
     * RENDER
     * =========================================================
     */

    return (
        <>
            <style>
                {shopHoursStyles}
            </style>


            <div className="shop-hours-page">


                {/* ================================================
                    INTRO
                ================================================ */}

                <section className="hours-intro">

                    <div className="hours-eyebrow">
                        Shop Hours
                    </div>


                    <h1>
                        Keep your opening hours
                        up to date
                    </h1>


                    <p>
                        These hours are shown to
                        shoppers on your OPPIES shop
                        profile, so keeping them current
                        helps shoppers know when to
                        visit.
                    </p>


                    {loadedShopName ? (

                        <div className="hours-active-shop">

                            <span className="hours-active-shop-dot" />

                            Showing hours for

                            <strong>
                                {loadedShopName}
                            </strong>

                        </div>

                    ) : null}

                </section>


                {/* ================================================
                    STATUS
                ================================================ */}

                {status.message ? (

                    <div
                        className={`hours-status ${status.type
                                ? `hours-status-${status.type}`
                                : ""
                            }`}
                    >

                        <div className="hours-status-icon">

                            {status.type ===
                                "success"
                                ? "✓"
                                : status.type ===
                                    "warning"
                                    ? "!"
                                    : status.type ===
                                        "error"
                                        ? "!"
                                        : "•"}

                        </div>


                        <span>
                            {status.message}
                        </span>

                    </div>

                ) : null}


                {/* ================================================
                    MAIN
                ================================================ */}

                <section className="hours-panel">


                    <div className="hours-panel-header">

                        <div>

                            <div className="hours-panel-eyebrow">
                                Weekly Schedule
                            </div>


                            <h2>
                                Opening Hours
                            </h2>


                            <p>
                                Enter your normal trading
                                hours for each day of the
                                week.
                            </p>

                        </div>


                        <div className="hours-header-actions">

                            <button
                                type="button"
                                className="hours-refresh-button"
                                onClick={
                                    refreshHours
                                }
                                disabled={
                                    loading ||
                                    saving
                                }
                                title="Reload current hours from OPPIES"
                            >

                                <svg
                                    viewBox="0 0 24 24"
                                    width="16"
                                    height="16"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <path d="M20 11a8.1 8.1 0 0 0-15.5-2M4 4v5h5" />

                                    <path d="M4 13a8.1 8.1 0 0 0 15.5 2M20 20v-5h-5" />
                                </svg>

                                Refresh

                            </button>


                            <div className="hours-header-icon">

                                <svg
                                    viewBox="0 0 24 24"
                                    width="22"
                                    height="22"
                                    fill="none"
                                    stroke="currentColor"
                                    strokeWidth="1.8"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                >
                                    <circle
                                        cx="12"
                                        cy="12"
                                        r="9"
                                    />

                                    <path d="M12 7v5l3 2" />
                                </svg>

                            </div>

                        </div>

                    </div>


                    {loading ? (

                        <div className="hours-loading">

                            <div className="hours-spinner" />


                            <span>
                                Loading current opening
                                hours from OPPIES...
                            </span>

                        </div>

                    ) : (

                        <>

                            {/* ==========================================
                                SHORTCUT
                            ========================================== */}

                            <div className="hours-shortcuts">

                                <div>

                                    <strong>
                                        Quick setup
                                    </strong>


                                    <span>
                                        If your weekday hours
                                        are the same, enter
                                        Monday first and copy
                                        them across.
                                    </span>

                                </div>


                                <button
                                    type="button"
                                    onClick={
                                        copyMondayToWeekdays
                                    }
                                    disabled={
                                        saving
                                    }
                                    className="hours-copy-button"
                                >

                                    <svg
                                        viewBox="0 0 24 24"
                                        width="15"
                                        height="15"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <rect
                                            x="8"
                                            y="8"
                                            width="11"
                                            height="11"
                                            rx="2"
                                        />

                                        <path d="M16 8V5a2 2 0 0 0-2-2H5a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h3" />
                                    </svg>

                                    Copy Monday to
                                    Tue–Fri

                                </button>

                            </div>


                            {/* ==========================================
                                DAYS
                            ========================================== */}

                            <div className="hours-list">

                                {DAYS_OF_WEEK.map(
                                    (
                                        day
                                    ) => {

                                        const isClosed =
                                            hours[
                                                day
                                            ]
                                                ?.trim()
                                                .toLowerCase() ===
                                            "closed";


                                        return (

                                            <div
                                                className={`hours-row ${isClosed
                                                        ? "hours-row-closed"
                                                        : ""
                                                    }`}
                                                key={
                                                    day
                                                }
                                            >

                                                <div className="hours-day">

                                                    <div className="hours-day-indicator" />


                                                    <strong>
                                                        {
                                                            day
                                                        }
                                                    </strong>

                                                </div>


                                                <div className="hours-input-wrap">

                                                    <input
                                                        type="text"
                                                        value={
                                                            hours[
                                                            day
                                                            ] ||
                                                            ""
                                                        }
                                                        disabled={
                                                            saving ||
                                                            isClosed
                                                        }
                                                        placeholder="e.g. 9am - 5pm"
                                                        maxLength={
                                                            60
                                                        }
                                                        onChange={(
                                                            event
                                                        ) =>
                                                            updateDay(
                                                                day,
                                                                event
                                                                    .target
                                                                    .value
                                                            )
                                                        }
                                                    />

                                                </div>


                                                <button
                                                    type="button"
                                                    className={`hours-closed-button ${isClosed
                                                            ? "hours-closed-button-active"
                                                            : ""
                                                        }`}
                                                    disabled={
                                                        saving
                                                    }
                                                    onClick={() =>
                                                        toggleClosed(
                                                            day
                                                        )
                                                    }
                                                >

                                                    {isClosed
                                                        ? "Closed"
                                                        : "Mark Closed"}

                                                </button>

                                            </div>
                                        );
                                    }
                                )}

                            </div>


                            {/* ==========================================
                                TIP
                            ========================================== */}

                            <div className="hours-tip">

                                <div className="hours-tip-icon">

                                    <svg
                                        viewBox="0 0 24 24"
                                        width="17"
                                        height="17"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="1.8"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                    >
                                        <circle
                                            cx="12"
                                            cy="12"
                                            r="9"
                                        />

                                        <path d="M12 11v5" />

                                        <path d="M12 8h.01" />
                                    </svg>

                                </div>


                                <div>

                                    <strong>
                                        Current OPPIES hours
                                    </strong>


                                    <span>
                                        The schedule above is
                                        loaded directly from the
                                        selected shop's OPPIES
                                        record. Make any changes
                                        you need and save when
                                        ready.
                                    </span>

                                </div>

                            </div>


                            {/* ==========================================
                                ACTIONS
                            ========================================== */}

                            <div className="hours-actions">

                                <div className="hours-change-state">

                                    {hasChanges ? (

                                        <>
                                            <span className="hours-change-dot" />

                                            Unsaved changes
                                        </>

                                    ) : (

                                        <>
                                            <span className="hours-saved-dot" />

                                            Hours up to date
                                        </>

                                    )}

                                </div>


                                <div className="hours-action-buttons">

                                    {hasChanges ? (

                                        <button
                                            type="button"
                                            className="hours-reset-button"
                                            onClick={
                                                resetChanges
                                            }
                                            disabled={
                                                saving
                                            }
                                        >
                                            Reset Changes
                                        </button>

                                    ) : null}


                                    <button
                                        type="button"
                                        className="hours-save-button"
                                        onClick={
                                            handleUpdate
                                        }
                                        disabled={
                                            saving ||
                                            !hasChanges
                                        }
                                    >

                                        {saving ? (

                                            <>
                                                <span className="hours-button-spinner" />

                                                Saving...
                                            </>

                                        ) : (

                                            <>
                                                <svg
                                                    viewBox="0 0 24 24"
                                                    width="16"
                                                    height="16"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    strokeWidth="1.9"
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                >
                                                    <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2Z" />

                                                    <path d="M17 21v-8H7v8" />

                                                    <path d="M7 3v5h8" />
                                                </svg>

                                                Save Hours
                                            </>

                                        )}

                                    </button>

                                </div>

                            </div>

                        </>
                    )}

                </section>

            </div>
        </>
    );
};


const shopHoursStyles = `

  .shop-hours-page {
    color: #1f2937;
    font-family:
      Inter,
      -apple-system,
      BlinkMacSystemFont,
      "Segoe UI",
      sans-serif;
  }

  .hours-intro {
    margin-bottom: 26px;
  }

  .hours-eyebrow,
  .hours-panel-eyebrow {
    color: #047857;
    font-size: 11px;
    font-weight: 800;
    letter-spacing: 0.14em;
    text-transform: uppercase;
  }

  .hours-intro h1 {
    max-width: 650px;
    margin: 7px 0;
    color: #17211c;
    font-size: clamp(26px, 3vw, 36px);
    line-height: 1.08;
    letter-spacing: -0.035em;
  }

  .hours-intro p {
    max-width: 650px;
    margin: 0;
    color: #64748b;
    font-size: 14px;
    line-height: 1.65;
  }

  .hours-active-shop {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    margin-top: 12px;
    padding: 7px 10px;
    border: 1px solid #d9ebe3;
    border-radius: 999px;
    background: #f5fbf8;
    color: #64748b;
    font-size: 10px;
    font-weight: 600;
  }

  .hours-active-shop strong {
    color: #047857;
    font-weight: 800;
  }

  .hours-active-shop-dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #10b981;
  }

  .hours-status {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 18px;
    padding: 12px 14px;
    border: 1px solid #e5e7eb;
    border-radius: 13px;
    background: #ffffff;
    color: #475569;
    font-size: 12px;
    font-weight: 600;
  }

  .hours-status-icon {
    display: grid;
    place-items: center;
    width: 25px;
    height: 25px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #f1f5f9;
    font-size: 12px;
    font-weight: 900;
  }

  .hours-status-success {
    border-color: #bbf7d0;
    background: #f0fdf4;
    color: #047857;
  }

  .hours-status-success .hours-status-icon {
    background: #dcfce7;
  }

  .hours-status-warning {
    border-color: #fde68a;
    background: #fffbeb;
    color: #92400e;
  }

  .hours-status-warning .hours-status-icon {
    background: #fef3c7;
  }

  .hours-status-error {
    border-color: #fecaca;
    background: #fef2f2;
    color: #b91c1c;
  }

  .hours-status-error .hours-status-icon {
    background: #fee2e2;
  }

  .hours-status-info {
    border-color: #d1fae5;
    background: #f0fdf4;
    color: #047857;
  }

  .hours-panel {
    padding: 22px;
    border: 1px solid #e5e7eb;
    border-radius: 20px;
    background: #ffffff;
    box-shadow:
      0 8px 24px rgba(15, 23, 42, 0.035);
  }

  .hours-panel-header {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
    margin-bottom: 20px;
  }

  .hours-panel-header h2 {
    margin: 4px 0 5px;
    color: #17211c;
    font-size: 20px;
    line-height: 1.2;
    letter-spacing: -0.025em;
  }

  .hours-panel-header p {
    margin: 0;
    color: #64748b;
    font-size: 12px;
    line-height: 1.55;
  }

  .hours-header-actions {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .hours-header-icon {
    display: grid;
    place-items: center;
    width: 42px;
    height: 42px;
    flex: 0 0 auto;
    border-radius: 13px;
    background: #ecfdf5;
    color: #047857;
  }

  .hours-refresh-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    min-height: 36px;
    padding: 7px 10px;
    border: 1px solid #d7e6df;
    border-radius: 10px;
    background: #ffffff;
    color: #047857;
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
  }

  .hours-refresh-button:hover:not(:disabled) {
    background: #f0fdf4;
  }

  .hours-refresh-button:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }

  .hours-loading {
    display: flex;
    min-height: 220px;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 11px;
    color: #94a3b8;
    font-size: 11px;
  }

  .hours-spinner {
    width: 28px;
    height: 28px;
    border: 3px solid #d1fae5;
    border-top-color: #047857;
    border-radius: 50%;
    animation: hours-spin 0.8s linear infinite;
  }

  .hours-shortcuts {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-bottom: 16px;
    padding: 13px 14px;
    border: 1px solid #e7e5df;
    border-radius: 13px;
    background: #fbf7ed;
  }

  .hours-shortcuts strong {
    display: block;
    margin-bottom: 2px;
    color: #334155;
    font-size: 11px;
  }

  .hours-shortcuts span {
    display: block;
    color: #64748b;
    font-size: 9px;
    line-height: 1.5;
  }

  .hours-copy-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 34px;
    flex: 0 0 auto;
    padding: 7px 10px;
    border: 1px solid #cde8dd;
    border-radius: 9px;
    background: #ffffff;
    color: #047857;
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
  }

  .hours-copy-button:hover:not(:disabled) {
    background: #f0fdf4;
  }

  .hours-list {
    overflow: hidden;
    border: 1px solid #e5e7eb;
    border-radius: 15px;
  }

  .hours-row {
    display: grid;
    grid-template-columns:
      145px
      minmax(180px, 1fr)
      110px;
    align-items: center;
    gap: 14px;
    min-height: 65px;
    padding: 10px 14px;
    border-bottom: 1px solid #eef2f0;
    background: #ffffff;
    transition: background 0.18s ease;
  }

  .hours-row:last-child {
    border-bottom: none;
  }

  .hours-row:hover {
    background: #fcfdfc;
  }

  .hours-row-closed {
    background: #fafafa;
  }

  .hours-day {
    display: flex;
    align-items: center;
    gap: 10px;
  }

  .hours-day-indicator {
    width: 7px;
    height: 7px;
    flex: 0 0 auto;
    border-radius: 50%;
    background: #047857;
  }

  .hours-row-closed .hours-day-indicator {
    background: #cbd5e1;
  }

  .hours-day strong {
    color: #334155;
    font-size: 11px;
  }

  .hours-input-wrap {
    min-width: 0;
  }

  .hours-input-wrap input {
    width: 100%;
    min-height: 39px;
    box-sizing: border-box;
    padding: 9px 11px;
    border: 1px solid #d8ddd9;
    border-radius: 10px;
    outline: none;
    background: #ffffff;
    color: #1f2937;
    font-family: inherit;
    font-size: 11px;
    transition:
      border-color 0.18s ease,
      box-shadow 0.18s ease;
  }

  .hours-input-wrap input:focus {
    border-color: #34a27a;
    box-shadow:
      0 0 0 3px rgba(4, 120, 87, 0.08);
  }

  .hours-input-wrap input:disabled {
    border-color: #e5e7eb;
    background: #f8fafc;
    color: #94a3b8;
  }

  .hours-closed-button {
    min-height: 34px;
    padding: 7px 9px;
    border: 1px solid #d1d5db;
    border-radius: 9px;
    background: #ffffff;
    color: #64748b;
    cursor: pointer;
    font-family: inherit;
    font-size: 9px;
    font-weight: 800;
  }

  .hours-closed-button:hover:not(:disabled) {
    background: #f8fafc;
  }

  .hours-closed-button-active {
    border-color: #d1fae5;
    background: #ecfdf5;
    color: #047857;
  }

  .hours-tip {
    display: flex;
    gap: 10px;
    margin-top: 16px;
    padding: 12px 13px;
    border-radius: 12px;
    background: #f8faf9;
  }

  .hours-tip-icon {
    display: grid;
    place-items: center;
    width: 31px;
    height: 31px;
    flex: 0 0 auto;
    border-radius: 9px;
    background: #ecfdf5;
    color: #047857;
  }

  .hours-tip strong {
    display: block;
    margin-bottom: 2px;
    color: #475569;
    font-size: 10px;
  }

  .hours-tip span {
    display: block;
    color: #94a3b8;
    font-size: 9px;
    line-height: 1.5;
  }

  .hours-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 18px;
    margin-top: 18px;
    padding-top: 17px;
    border-top: 1px solid #eef2f0;
  }

  .hours-change-state {
    display: flex;
    align-items: center;
    gap: 7px;
    color: #64748b;
    font-size: 9px;
    font-weight: 700;
  }

  .hours-change-dot,
  .hours-saved-dot {
    width: 7px;
    height: 7px;
    border-radius: 50%;
  }

  .hours-change-dot {
    background: #f59e0b;
  }

  .hours-saved-dot {
    background: #10b981;
  }

  .hours-action-buttons {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  .hours-reset-button,
  .hours-save-button {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 7px;
    min-height: 39px;
    padding: 9px 13px;
    border-radius: 10px;
    cursor: pointer;
    font-family: inherit;
    font-size: 10px;
    font-weight: 800;
  }

  .hours-reset-button {
    border: 1px solid #d1d5db;
    background: #ffffff;
    color: #64748b;
  }

  .hours-reset-button:hover:not(:disabled) {
    background: #f8fafc;
  }

  .hours-save-button {
    border: none;
    background: #047857;
    color: #ffffff;
    box-shadow:
      0 7px 18px rgba(4, 120, 87, 0.16);
  }

  .hours-save-button:hover:not(:disabled) {
    background: #036b4f;
  }

  .hours-reset-button:disabled,
  .hours-save-button:disabled,
  .hours-copy-button:disabled,
  .hours-closed-button:disabled {
    cursor: not-allowed;
    opacity: 0.55;
  }

  .hours-button-spinner {
    width: 11px;
    height: 11px;
    border: 2px solid rgba(255, 255, 255, 0.4);
    border-top-color: #ffffff;
    border-radius: 50%;
    animation: hours-spin 0.7s linear infinite;
  }

  @keyframes hours-spin {
    to {
      transform: rotate(360deg);
    }
  }

  @media (max-width: 760px) {

    .hours-row {
      grid-template-columns:
        110px
        minmax(0, 1fr);
    }

    .hours-closed-button {
      grid-column: 2;
      width: max-content;
    }

    .hours-shortcuts {
      align-items: stretch;
      flex-direction: column;
    }

    .hours-copy-button {
      align-self: flex-start;
    }

  }

  @media (max-width: 560px) {

    .hours-panel {
      padding: 17px;
      border-radius: 17px;
    }

    .hours-panel-header {
      align-items: flex-start;
    }

    .hours-header-actions {
      flex-direction: column-reverse;
      align-items: flex-end;
    }

    .hours-row {
      grid-template-columns: 1fr;
      gap: 8px;
      padding: 13px;
    }

    .hours-closed-button {
      grid-column: 1;
    }

    .hours-actions {
      align-items: stretch;
      flex-direction: column;
    }

    .hours-action-buttons {
      width: 100%;
    }

    .hours-reset-button,
    .hours-save-button {
      flex: 1;
    }

  }

  @media (max-width: 390px) {

    .hours-action-buttons {
      flex-direction: column;
    }

    .hours-reset-button,
    .hours-save-button {
      width: 100%;
    }

  }

`;

export default ShopHoursTab;